import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import { supabase, isSupabaseConfigured } from '../utils/supabase';
import { useAuth } from './AuthContext';
import { fetchAccessibleOwnerIds } from '../utils/recordVisibility';
import {
  CONTACT_TYPES,
  CONTACT_TAGS,
  CONTACT_SOURCES,
  DEAL_PARTY_ROLES,
  NETWORK_RELATIONSHIP_TYPES,
  DISPOSITIONS,
  NEXT_ACTIONS,
  STAGES,
} from '../constants/crmConstants';

/**
 * CRM Settings Context
 * 
 * Provides global access to admin-configurable CRM settings.
 * 
 * HYBRID ARCHITECTURE:
 * - Tags → Loaded from Supabase crm_tags table
 * - Deal Stages → Loaded from Supabase crm_deal_stages table
 * - Everything else → Loaded from Firebase config/crmSettings
 *
 * TAGS ARE ONE NAMESPACE:
 * Contact tags and deal tags are the same thing — crm_contacts.tags is the
 * store and crm_deals.tags mirrors it. `tags` is the canonical list;
 * contactTags/dealTags and the per-entity getVisible/getAll pairs are
 * deprecated aliases onto it, kept so existing call sites keep working.
 *
 * TAG VISIBILITY:
 * - Global tags (visibility='global') → Visible to everyone
 * - Personal tags (visibility='personal') → Visible to owner + admins only
 * - Orphan tags (discovered from data) → Visible based on data ownership
 *
 * Usage:
 *   const { contactTypes, dealStages, loading } = useCrmSettings();
 *   // For tags with visibility filtering:
 *   const { getVisibleTags } = useCrmSettings();
 *   const visibleTags = getVisibleTags(userId, isCRMAdmin);
 */

const CrmSettingsContext = createContext(null);

/**
 * Add a `displayLabel` to each tag, disambiguating any labels that collide.
 *
 * Two tags can share a label while storing different values (for example a
 * kebab-case and a title-case spelling of the same name). In a dropdown they
 * look identical but filter differently, so the raw value is appended to the
 * colliding ones — the same rule crm_distinct_deal_tags() applies for the
 * Analytics page. `label` is left untouched so TagSelector and other consumers
 * keep showing the clean name.
 */
function withDisplayLabels(tags) {
  const countByLabel = new Map();
  tags.forEach(t => {
    const key = String(t.label || '').trim().toLowerCase();
    countByLabel.set(key, (countByLabel.get(key) || 0) + 1);
  });

  return tags.map(t => {
    const key = String(t.label || '').trim().toLowerCase();
    return {
      ...t,
      displayLabel: countByLabel.get(key) > 1 ? `${t.label} (${t.value})` : t.label,
    };
  });
}

// Default settings (used if config doesn't exist)
// Note: contactTags, dealTags, dealStages, and contactTypes are loaded from Supabase
const DEFAULT_CRM_SETTINGS = {
  contactSources: CONTACT_SOURCES,
  dealPartyRoles: DEAL_PARTY_ROLES,
  networkRelationshipTypes: NETWORK_RELATIONSHIP_TYPES,
  customContactFields: [],
  dispositions: DISPOSITIONS,
  nextActions: NEXT_ACTIONS,
  // Multi-owner rollout flag (Phase 6). Admins can always manage teams/shares;
  // non-admin self-serve is dark until selfServe flips true (or the uid is in
  // the pilot list). Mirrored server-side in manageCrmTeam.
  teamSharing: {
    selfServe: false,
    pilotOwnerUids: [],
  },
  // Org-level quiet-hours for campaign sends (enforced server-side in
  // processAutomationQueue via sendWindow.js). Default mirrors the backend.
  campaignSendWindow: {
    enabled: true,
    timezoneMode: 'recipient',
    fixedTimezone: 'America/New_York',
    sms: { days: [1, 2, 3, 4, 5, 6], start: '09:00', end: '20:00' },
    email: { days: [1, 2, 3, 4, 5], start: '08:00', end: '18:00' },
    holidays: [],
  },
  updatedAt: null,
  updatedBy: null,
};

// Default deal stages (fallback if Supabase not configured or empty)
const DEFAULT_DEAL_STAGES = STAGES;

// Default tags (fallback if Supabase not configured or empty). One list, since
// contact and deal tags share a namespace.
const DEFAULT_TAGS = [
  ...CONTACT_TAGS,
  { value: 'hot', label: 'Hot', color: 'red' },
  { value: 'pre-approval', label: 'Pre-Approval', color: 'green' },
  { value: 'rate-lock', label: 'Rate Lock', color: 'orange' },
  { value: 'rush', label: 'Rush', color: 'red' },
  { value: 'at-risk', label: 'At Risk', color: 'red' },
].filter((tag, index, all) => all.findIndex(t => t.value === tag.value) === index);

export function CrmSettingsProvider({ children }) {
  // Firebase settings (contactSources, dealPartyRoles, etc. — NOT contactTypes)
  const [settings, setSettings] = useState(DEFAULT_CRM_SETTINGS);
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  
  // Supabase contact types (separate state — replaces Firebase contactTypes)
  const [contactTypes, setContactTypes] = useState(CONTACT_TYPES);
  const [contactTypesLoading, setContactTypesLoading] = useState(true);
  
  // Supabase tags — one namespace shared by contacts and deals
  const [tags, setTags] = useState(DEFAULT_TAGS);
  const [tagsLoading, setTagsLoading] = useState(true);
  
  // Supabase deal stages (separate state)
  const [dealStages, setDealStages] = useState(DEFAULT_DEAL_STAGES);
  const [stagesLoading, setStagesLoading] = useState(true);

  // Supabase dispositions (separate state, falls back to Firestore/defaults)
  const [dispositions, setDispositions] = useState(DISPOSITIONS);
  const [dispositionsLoading, setDispositionsLoading] = useState(true);
  
  const [error, setError] = useState(null);

  // Book shares (multi-owner Phase 2): owner uids whose books are shared with
  // the signed-in user. Folded into tag visibility so a grantee can see and
  // filter by the tags that exist on the shared book — query-side expansion,
  // no crm_tags writes, so revocation narrows instantly.
  const { currentUser } = useAuth();
  const [accessibleTagOwnerIds, setAccessibleTagOwnerIds] = useState([]);

  // Unique instance ID to prevent Supabase channel collisions if this provider
  // is ever accidentally mounted more than once.
  const instanceId = React.useRef(Math.random().toString(36).slice(2, 8));

  // Combined loading state
  const loading = firebaseLoading || tagsLoading || stagesLoading || contactTypesLoading || dispositionsLoading;

  // =========================================================================
  // Accessible owners for tag visibility (book shares)
  // Tags are one namespace across contacts and deals, so take the union of
  // both entity types. Kept fresh by the same crm_team_members realtime
  // pattern useVisibilityScope uses, so a revoke narrows the tag list live.
  // =========================================================================
  const viewerUid = currentUser?.uid || null;
  const [tagOwnersVersion, setTagOwnersVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!viewerUid || !isSupabaseConfigured()) {
      setAccessibleTagOwnerIds([]);
      return undefined;
    }
    Promise.all([
      fetchAccessibleOwnerIds(viewerUid, 'contact'),
      fetchAccessibleOwnerIds(viewerUid, 'deal'),
    ])
      .then(([contactOwners, dealOwners]) => {
        if (cancelled) return;
        const union = [...new Set([...(contactOwners || []), ...(dealOwners || [])])];
        setAccessibleTagOwnerIds(union);
      })
      .catch(() => {
        if (!cancelled) setAccessibleTagOwnerIds([viewerUid]);
      });
    return () => {
      cancelled = true;
    };
  }, [viewerUid, tagOwnersVersion]);

  useEffect(() => {
    if (!viewerUid || !isSupabaseConfigured()) return undefined;
    const channel = supabase
      .channel(`crm-settings-tag-owners-${viewerUid}-${instanceId.current}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_team_members',
          filter: `member_uid=eq.${viewerUid}`,
        },
        () => setTagOwnersVersion((v) => v + 1)
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [viewerUid]);

  // =========================================================================
  // Load contact types from Supabase (crm_contact_types)
  // Replaces Firebase config/crmSettings.contactTypes
  // =========================================================================
  const loadContactTypesFromSupabase = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      console.warn('[CrmSettingsContext] Supabase not configured, using default contact types');
      setContactTypesLoading(false);
      return;
    }

    try {
      const { data: types, error: typesError } = await supabase
        .from('crm_contact_types')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (typesError) {
        // Table might not exist yet - fall back to defaults (Firebase/constants)
        console.warn('[CrmSettingsContext] Could not load contact types from Supabase:', typesError.message);
        console.warn('[CrmSettingsContext] Falling back to Firebase/default contact types');
        setContactTypesLoading(false);
        return;
      }

      // Transform to expected format
      const typesList = (types || []).map(t => ({
        value: t.value,
        label: t.label,
        color: t.color || 'gray',
        icon: t.icon || null,
        description: t.description || null,
        id: t.id,
        sortOrder: t.sort_order,
      }));

      console.log('[CrmSettingsContext] Loaded contact types from Supabase:', typesList.map(t => ({ value: t.value, label: t.label })));

      // Only update if we got data (otherwise keep defaults)
      if (typesList.length > 0) {
        setContactTypes(typesList);
      } else {
        console.warn('[CrmSettingsContext] crm_contact_types table is empty, keeping defaults');
      }

      setContactTypesLoading(false);
    } catch (err) {
      console.error('[CrmSettingsContext] Error loading contact types from Supabase:', err);
      setContactTypesLoading(false);
    }
  }, []);

  // =========================================================================
  // Load tags from Supabase
  // Combines registered tags (crm_tags) with server-side orphan discovery via
  // the get_distinct_entity_tags RPC, which unions contact and deal tag values.
  // =========================================================================
  const loadTagsFromSupabase = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, using default tags');
      setTagsLoading(false);
      return;
    }

    // Swallow rejections per-query so one failed RPC can't sink the others
    // (supabase-js returns { data, error } for query errors, but network-level
    // failures reject).
    const safe = async (query) => {
      try {
        return await query;
      } catch (err) {
        return { data: null, error: err };
      }
    };

    try {
      // The three loads are independent — one round trip instead of three, so
      // realtime-triggered reloads land fast enough to feel instant.
      const [registryRes, discoveredRes, usageRes] = await Promise.all([
        safe(
          supabase
            .from('crm_tags')
            .select('*')
            .eq('is_active', true)
            .order('label', { ascending: true })
        ),
        safe(supabase.rpc('get_distinct_entity_tags', { p_entity_type: null })),
        safe(supabase.rpc('crm_tag_usage_counts')),
      ]);

      if (registryRes.error) {
        // Keep whatever we already have rather than wiping the list
        console.warn('Could not load tags from Supabase:', registryRes.error.message);
        setTagsLoading(false);
        return;
      }

      const registered = (registryRes.data || []).map(t => ({
        value: t.value,
        label: t.label || t.value,
        color: t.color,
        visibility: t.visibility || 'global',
        ownerId: t.owner_id || null,
        ownerIds: t.owner_ids || [],
      }));

      // Discover tags applied to records but missing from the registry
      let orphans = [];
      if (discoveredRes.error) {
        console.warn('Could not discover orphan tags via RPC:', discoveredRes.error);
      } else if (discoveredRes.data) {
        const registeredValues = new Set(registered.map(t => t.value));
        orphans = discoveredRes.data
          .filter(row => row.tag_value && !registeredValues.has(row.tag_value))
          .map(row => ({
            value: row.tag_value,
            label: row.tag_value,
            color: 'gray',
            visibility: 'discovered',
            ownerIds: row.owner_ids || [],
          }));
      }

      if (orphans.length > 0) {
        console.info(`Found ${orphans.length} orphan tags:`, orphans.map(t => t.value));
      }

      // Per-tag usage counts, so dropdowns can show how many records a tag
      // actually matches instead of silently returning nothing.
      let usageByValue = new Map();
      if (usageRes.error) {
        console.warn('Could not load tag usage counts via RPC:', usageRes.error);
      } else if (usageRes.data) {
        usageByValue = new Map(usageRes.data.map(row => [row.tag_value, row]));
      }

      const tagsList = [...registered, ...orphans]
        .map(t => {
          const usage = usageByValue.get(t.value);
          return {
            ...t,
            dealCount: usage ? Number(usage.deal_count) : 0,
            contactCount: usage ? Number(usage.contact_count) : 0,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      // Unconditional on success: an empty registry must clear the list
      // (deleting the last tag previously left the stale list on screen).
      setTags(tagsList);

      setTagsLoading(false);
    } catch (err) {
      console.error('Error loading tags from Supabase:', err);
      setTagsLoading(false);
    }
  }, []);

  // =========================================================================
  // Load deal stages from Supabase
  // =========================================================================
  const loadStagesFromSupabase = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, using default deal stages');
      setStagesLoading(false);
      return;
    }

    try {
      const { data: stages, error: stagesError } = await supabase
        .from('crm_deal_stages')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (stagesError) {
        // Table might not exist yet - fall back to defaults
        console.warn('Could not load deal stages from Supabase:', stagesError.message);
        setStagesLoading(false);
        return;
      }

      // Transform to expected format
      const stagesList = (stages || []).map(s => ({
        value: s.value,
        label: s.label,
        color: s.color,
        id: s.id, // Include ID for edit/delete operations
        sortOrder: s.sort_order,
        // is_hidden keeps a stage loaded (labels/colors resolve, valid for Arive
        // sync) while removing it from manual stage-selection menus.
        isHidden: s.is_hidden || false,
      }));

      // Only update if we got data
      if (stagesList.length > 0) {
        setDealStages(stagesList);
      }

      setStagesLoading(false);
    } catch (err) {
      console.error('Error loading deal stages from Supabase:', err);
      setStagesLoading(false);
    }
  }, []);

  // =========================================================================
  // Load dispositions from Supabase (falls back to Firestore/defaults)
  // =========================================================================
  const loadDispositionsFromSupabase = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setDispositionsLoading(false);
      return;
    }

    try {
      const { data: rows, error: dispError } = await supabase
        .from('crm_dispositions')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (dispError) {
        console.warn('Could not load dispositions from Supabase:', dispError.message);
        setDispositionsLoading(false);
        return;
      }

      const list = (rows || []).map(d => ({
        value: d.value,
        label: d.label,
        color: d.color,
        id: d.id,
        sortOrder: d.sort_order,
      }));

      if (list.length > 0) {
        setDispositions([{ value: '', label: 'Select Disposition' }, ...list]);
      }

      setDispositionsLoading(false);
    } catch (err) {
      console.error('Error loading dispositions from Supabase:', err);
      setDispositionsLoading(false);
    }
  }, []);

  // =========================================================================
  // Supabase tags subscription (real-time updates)
  // =========================================================================
  useEffect(() => {
    // Initial load
    loadTagsFromSupabase();

    // Set up real-time subscription
    if (!isSupabaseConfigured()) return;

    // Debounced reload: every tag apply touches crm_tags.owner_ids and a CSV
    // import registers rows in bursts — coalesce an event storm into one
    // reload instead of a reload per event.
    let reloadTimer = null;
    const scheduleReload = () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      reloadTimer = setTimeout(() => {
        reloadTimer = null;
        loadTagsFromSupabase();
      }, 500);
    };

    const subscription = supabase
      .channel(`crm-tags-changes-${instanceId.current}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_tags',
        },
        scheduleReload
      )
      .subscribe();

    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      subscription.unsubscribe();
    };
  }, [loadTagsFromSupabase]);

  // =========================================================================
  // Supabase deal stages subscription (real-time updates)
  // =========================================================================
  useEffect(() => {
    // Initial load
    loadStagesFromSupabase();

    // Set up real-time subscription
    if (!isSupabaseConfigured()) return;

    const subscription = supabase
      .channel(`crm-deal-stages-changes-${instanceId.current}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deal_stages',
        },
        () => {
          // Reload stages when any change occurs
          loadStagesFromSupabase();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [loadStagesFromSupabase]);

  // =========================================================================
  // Supabase dispositions subscription (real-time updates)
  // =========================================================================
  useEffect(() => {
    loadDispositionsFromSupabase();

    if (!isSupabaseConfigured()) return;

    const subscription = supabase
      .channel(`crm-dispositions-changes-${instanceId.current}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crm_dispositions' },
        () => { loadDispositionsFromSupabase(); }
      )
      .subscribe();

    return () => { subscription.unsubscribe(); };
  }, [loadDispositionsFromSupabase]);

  // =========================================================================
  // Supabase contact types subscription (real-time updates)
  // =========================================================================
  useEffect(() => {
    // Initial load
    loadContactTypesFromSupabase();

    // Set up real-time subscription
    if (!isSupabaseConfigured()) return;

    const subscription = supabase
      .channel(`crm-contact-types-changes-${instanceId.current}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_contact_types',
        },
        () => {
          // Reload contact types when any change occurs
          loadContactTypesFromSupabase();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [loadContactTypesFromSupabase]);

  // =========================================================================
  // Firebase settings subscription (contactSources, dealPartyRoles, etc.)
  // Note: contactTypes, Tags, and Deal Stages now come from Supabase
  // =========================================================================
  useEffect(() => {
    const settingsRef = doc(db, 'config', 'crmSettings');
    
    const unsubscribe = onSnapshot(
      settingsRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Merge with defaults, but EXCLUDE items that come from Supabase
          // - contactTypes → crm_contact_types table
          // - contactTags, dealTags → crm_tags table
          // - dealStages → crm_deal_stages table
          const { 
            contactTypes: _ct,
            contactTags: _, 
            dealTags: __, 
            tags: ___, 
            dealStages: ____, 
            ...restData 
          } = data;
          
          setSettings({
            ...DEFAULT_CRM_SETTINGS,
            ...restData,
          });
          
          setFirebaseLoading(false);
          setError(null);
        } else {
          console.warn('CRM Settings not found in Firestore, using defaults');
          setSettings(DEFAULT_CRM_SETTINGS);
          setFirebaseLoading(false);
        }
      },
      (err) => {
        console.error('Error loading CRM settings:', err);
        setError(err.message);
        setSettings(DEFAULT_CRM_SETTINGS);
        setFirebaseLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // =========================================================================
  // Helper methods for common lookups
  // =========================================================================
  const getContactType = (value) => {
    return contactTypes.find(t => t.value === value) || null;
  };

  const getTag = (value) => {
    return tags.find(t => t.value === value) || null;
  };

  const getDealPartyRole = (value) => {
    return settings.dealPartyRoles.find(r => r.value === value) || null;
  };

  const getNetworkRelationshipType = (value) => {
    return settings.networkRelationshipTypes.find(t => t.value === value) || null;
  };

  const getCustomField = (fieldName) => {
    return settings.customContactFields.find(f => f.fieldName === fieldName) || null;
  };

  const getDisposition = (value) => {
    return dispositions.find(d => d.value === value) || null;
  };

  const getNextAction = (value) => {
    return settings.nextActions.find(a => a.value === value) || null;
  };

  const getDealStage = (value) => {
    return dealStages.find(s => s.value === value) || null;
  };

  // Build the list of stages for a manual selection menu (DealPage dropdown,
  // Quick Stage Change, Bulk Edit). Excludes hidden stages, but always keeps
  // the deal's current stage available so it displays correctly and can't be
  // silently overwritten on save (e.g., when Arive set a stage that is hidden).
  const getStagesForPicker = useCallback((currentValue) => {
    const visible = dealStages.filter(s => !s.isHidden);
    if (!currentValue || visible.some(s => s.value === currentValue)) {
      return visible;
    }
    const found = dealStages.find(s => s.value === currentValue);
    return [...visible, found || { value: currentValue, label: currentValue, color: 'gray' }];
  }, [dealStages]);

  // Check if a contact type is allowed for a deal party role
  const isContactTypeAllowedForRole = (contactType, roleValue) => {
    const role = getDealPartyRole(roleValue);
    if (!role) return false;
    return role.contactTypesAllowed.includes(contactType);
  };

  // Refresh contact types from Supabase (call after adding/updating/deleting types)
  const refreshContactTypes = useCallback(() => {
    loadContactTypesFromSupabase();
  }, [loadContactTypesFromSupabase]);

  // Refresh tags from Supabase (call after adding/updating tags)
  const refreshTags = useCallback(() => {
    loadTagsFromSupabase();
  }, [loadTagsFromSupabase]);

  // Refresh deal stages from Supabase (call after adding/updating/deleting stages)
  const refreshStages = useCallback(() => {
    loadStagesFromSupabase();
  }, [loadStagesFromSupabase]);

  // =========================================================================
  // Tag visibility filtering
  // Returns tags visible to a specific user based on their role
  // =========================================================================
  
  /**
   * Filter tags based on user visibility rules:
   * - Admins see ALL tags (global + all personal + all discovered)
   * - Non-admins see: global + their own personal + tags they have access to via ownerIds
   * 
   * Dynamic Tag Discovery: When a tag is applied to a user's contact/deal,
   * that user is added to the tag's ownerIds array, granting them visibility.
   * 
   * Book shares widen this: when filtering for the signed-in user, every
   * owner uid accessible to them (crm_accessible_owner_ids) counts as "them",
   * so grantees can see/filter the tags that exist on a shared book.
   *
   * @param {Array} allTags - Full list of tags with visibility metadata
   * @param {string} userId - Current user's Firebase UID
   * @param {boolean} isAdmin - Whether user is CRM Admin or SuperAdmin
   * @returns {Array} Filtered tags visible to the user
   */
  const filterTagsByVisibility = useCallback((allTags, userId, isAdmin) => {
    if (!allTags || allTags.length === 0) return [];

    // Expand the viewer to their accessible owners (book shares). Only applies
    // when filtering for the signed-in user — other uids keep exact semantics.
    const selfIds =
      userId && userId === viewerUid && accessibleTagOwnerIds.length > 0
        ? new Set([userId, ...accessibleTagOwnerIds])
        : new Set(userId ? [userId] : []);
    const matchesSelf = (uid) => uid && selfIds.has(uid);
    const anyMatchesSelf = (uids) => Array.isArray(uids) && uids.some((u) => selfIds.has(u));
    
    // Admins see everything
    if (isAdmin) {
      return withDisplayLabels(allTags.map(t => ({
        value: t.value,
        label: t.label,
        color: t.color,
        dealCount: t.dealCount || 0,
        contactCount: t.contactCount || 0,
        isPersonal: t.visibility === 'personal',
        isDiscovered: t.visibility === 'discovered',
      })));
    }
    
    // Non-admins see filtered tags
    const visible = allTags
      .filter(tag => {
        // Global tags are visible to everyone
        if (tag.visibility === 'global') return true;
        
        // Personal tags are visible to:
        // 1. The tag creator (ownerId)
        // 2. Any user in ownerIds (users who have this tag on their data)
        // — both expanded to the viewer's accessible owners (book shares)
        if (tag.visibility === 'personal') {
          if (matchesSelf(tag.ownerId)) return true;
          if (anyMatchesSelf(tag.ownerIds)) return true;
        }
        
        // Discovered tags are visible if the user (or a book shared with
        // them) has data with that tag
        if (tag.visibility === 'discovered' && anyMatchesSelf(tag.ownerIds)) return true;
        
        return false;
      });

    // Labels are disambiguated against the visible subset, so a user who can see
    // only one side of a collision still gets the clean name.
    return withDisplayLabels(visible.map(t => ({
      value: t.value,
      label: t.label,
      color: t.color,
      dealCount: t.dealCount || 0,
      contactCount: t.contactCount || 0,
      isPersonal: t.visibility === 'personal',
      isDiscovered: t.visibility === 'discovered',
    })));
  }, [viewerUid, accessibleTagOwnerIds]);

  /**
   * Get tags visible to the current user
   * @param {string} userId - Current user's Firebase UID
   * @param {boolean} isAdmin - Whether user is CRM Admin or SuperAdmin
   * @returns {Array} Visible tags
   */
  const getVisibleTags = useCallback((userId, isAdmin = false) => {
    return filterTagsByVisibility(tags, userId, isAdmin);
  }, [tags, filterTagsByVisibility]);

  /**
   * Get ALL tags (for admin views like TagsManager)
   * Includes visibility metadata for display
   */
  const getAllTags = useCallback(() => {
    return withDisplayLabels(tags.map(t => ({
      ...t,
      isPersonal: t.visibility === 'personal',
      isDiscovered: t.visibility === 'discovered',
      isGlobal: t.visibility === 'global',
    })));
  }, [tags]);

  // Deal stages with hidden ones removed — for manual selection menus.
  // `dealStages` (full list) is still used for badge/label/color lookups.
  const visibleDealStages = dealStages.filter(s => !s.isHidden);

  const value = {
    // Firebase settings (contactSources, dealPartyRoles, etc.)
    ...settings,
    
    // Supabase contact types (overrides settings.contactTypes from spread above)
    contactTypes,
    
    // Supabase tags - RAW (includes all tags with visibility metadata).
    // Use getVisibleTags for filtered access.
    tags,
    // Deprecated aliases: contact and deal tags are one namespace now, so both
    // resolve to the same list. Kept so existing call sites keep working.
    contactTags: tags,
    dealTags: tags,
    
    // Supabase deal stages
    dealStages,            // full list (incl. hidden) — for rendering/lookups
    visibleDealStages,     // hidden removed — for manual pickers
    
    // Supabase dispositions (overrides the Firestore ones from ...settings)
    dispositions,
    
    // Status
    loading,
    contactTypesLoading,
    tagsLoading,
    stagesLoading,
    dispositionsLoading,
    error,
    
    // Helper methods
    getContactType,
    getTag,
    getContactTag: getTag, // deprecated alias
    getDealTag: getTag,    // deprecated alias
    getDealPartyRole,
    getNetworkRelationshipType,
    getCustomField,
    getDisposition,
    getNextAction,
    getDealStage,
    getStagesForPicker,
    isContactTypeAllowedForRole,
    refreshContactTypes,
    refreshTags,
    refreshStages,
    refreshDispositions: loadDispositionsFromSupabase,
    
    // Tag visibility methods
    getVisibleTags,            // (userId, isAdmin) => filtered tags for dropdowns
    getAllTags,                // () => all tags with metadata (for admin views)
    // Deprecated aliases onto the unified list.
    getVisibleContactTags: getVisibleTags,
    getVisibleDealTags: getVisibleTags,
    getAllContactTags: getAllTags,
    getAllDealTags: getAllTags,
  };

  return (
    <CrmSettingsContext.Provider value={value}>
      {children}
    </CrmSettingsContext.Provider>
  );
}

// Wrapper component for easier importing
export function CrmSettingsProviderWrapper({ children }) {
  return (
    <CrmSettingsProvider>
      {children}
    </CrmSettingsProvider>
  );
}

// Custom hook for consuming the context
export function useCrmSettings() {
  const context = useContext(CrmSettingsContext);
  
  if (context === null) {
    throw new Error('useCrmSettings must be used within a CrmSettingsProvider');
  }
  
  return context;
}

export { CrmSettingsContext };
export default useCrmSettings;

